import { isCatalogItemType } from "building-types/catalogItemTools";
import { DOC_CACHE_SIZE, DOC_ENTITY_NODE_CACHE_SIZE, type Doc } from "building-types/doc";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { isDocEntityType } from "building-types/handles/docEntityHandleTypeTools";
import { LRUObjectCache } from "common/utils/lruObjectCache";
import { docGetCatalogItemParentCatalog } from "../docGetCatalogItem";
import { docCollectSystemIds } from "./docCollectSystemIds";
import docEntityGetParent from "./docEntityGetParent";
import { DocEntityHandleSet } from "./DocEntityHandleSet";
import type { DocEntityNode } from "./docEntityNode";
import { type DocEntityReference } from "./docEntityReferenceTypes";
import { docGetEntityHandleById } from "./docGetEntityHandleById";
import { buildForeignKeyReferenceGroups } from "./docSchemaForeignKeyUtils";

type ReferenceValidationResult = { valid: DocEntityHandle[]; invalid: DocEntityReference[] };
type PerDocReferenceValidationCache = LRUObjectCache<string, ReferenceValidationResult>;

/**
 * Keyed on {@link Doc} because reference validity is doc-dependent: the same node handle
 * resolves to a different valid/invalid set across docs depending on which referenced
 * entities each doc contains. Two docs routinely coexist in memory.
 */
const referenceValidationCache = new LRUObjectCache<Doc, PerDocReferenceValidationCache>(
  DOC_CACHE_SIZE,
  (a, b) => a === b,
  (perDocCache) => perDocCache.clear(),
);

export function validateDocEntityNodeReferences(
  doc: Doc,
  node: DocEntityNode,
  fkReferences?: DocEntityReference[],
  precomputedSystemIds?: Set<string>,
): ReferenceValidationResult {
  const cacheKey = node.getHandleHash();
  let perDocCache = referenceValidationCache.get(doc);
  if (!perDocCache) {
    perDocCache = new LRUObjectCache(DOC_ENTITY_NODE_CACHE_SIZE);
    referenceValidationCache.add(doc, perDocCache);
  }

  const cached = perDocCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const valid = new DocEntityHandleSet();
  const invalid: DocEntityReference[] = [];
  const systemIds = precomputedSystemIds ?? docCollectSystemIds(doc);

  const references = fkReferences ?? node.getForeignKeyReferences();
  const groupedReferences = buildForeignKeyReferenceGroups(references);

  const markRefInvalid = (ref: DocEntityReference, fieldPaths: Set<string>) => {
    if (!ref.referencedEntityId) {
      invalid.push(ref);
      return;
    }

    if (!ref.referencedEntityType) {
      throw new Error("referencedEntityType is required");
    }

    if (systemIds.has(ref.referencedEntityId)) {
      return;
    }

    for (const fieldPath of fieldPaths) {
      invalid.push({ ...ref, fieldPath });
    }
  };

  const refGroups = Array.from(groupedReferences.values());

  for (const ref of refGroups) {
    const referencedEntityType = ref.candidateTypes.values().next().value;

    if (!ref.referencedEntityId) {
      for (const fieldPath of ref.fieldPaths) {
        invalid.push({
          ...ref,
          fieldPath,
          referencedEntityId: undefined,
          referencedEntityType,
        });
      }
      continue;
    }

    let candidateTypes: DocEntityReference["entityType"][] = [];

    // If we weren't able to determine the referenced entity type, we need to do a full doc scan to look up the handle
    if (referencedEntityType !== "INDETERMINATE_ENTITY_TYPE") {
      const rawCandidateTypes = Array.from(ref.candidateTypes);
      candidateTypes = rawCandidateTypes.filter(isDocEntityType);

      // Check if the field may be a settings-backed catalog item
      if (rawCandidateTypes.some(isCatalogItemType)) {
        candidateTypes = [...candidateTypes, "CATALOG_ITEM"];
      }
    }

    const { candidateTypes: omittedCandidateTypes, fieldPaths: omittedFieldPaths, ...refWithoutGroupOnlyFields } = ref;
    void omittedCandidateTypes;
    void omittedFieldPaths;
    const reference: DocEntityReference = {
      ...(refWithoutGroupOnlyFields as DocEntityReference),
      referencedEntityType,
    };

    const handle: DocEntityHandle | undefined | null = docGetEntityHandleById(
      doc,
      ref.referencedEntityId,
      candidateTypes.filter((type) => type !== undefined),
    );

    if (!handle) {
      markRefInvalid(reference, ref.fieldPaths);
      continue;
    }

    /**
     * Resolve the chain from catalog item -> catalog settings -> catalog parent settings set
     */

    if (handle.type === "CATALOG_ITEM" && isCatalogItemType(referencedEntityType)) {
      valid.add(handle);
      const catalogHandle = docGetCatalogItemParentCatalog(doc, handle);

      if (catalogHandle) {
        valid.add(catalogHandle);

        const catalogParentSettingsSet = docEntityGetParent(doc, catalogHandle);

        if (catalogParentSettingsSet?.type === "SETTINGS_SET") {
          valid.add(catalogParentSettingsSet);
        }
      }
      continue;
    } else if (handle) {
      valid.add(handle);
    }
  }

  const result = { valid: valid.toArray(), invalid };

  perDocCache.add(cacheKey, result);

  return result;
}

export function clearReferenceValidationCache(doc?: Doc): void {
  if (doc) {
    referenceValidationCache.delete(doc);
    return;
  }
  referenceValidationCache.clear();
}
